import { Link } from "react-router-dom";
import { MapPin, Building2, Target, UserCheck } from "lucide-react";
import CategoryBadge from "./CategoryBadge";
import TerritoryLabel from "../common/TerritoryLabel";
import { joinParts } from "../../lib/vocabulary";

export default function DoctorCard({ doctor, actions = null }) {
  // A doctor may be owned by a Sales Officer, a Sales Manager, or both.
  const owners = [doctor?.assigned_to_user?.name, doctor?.assigned_manager_user?.name].filter(Boolean);
  const assignedName = owners.length ? owners.join(" + ") : "Unassigned";
  const isCatA = doctor.category === "A";
  const archived = doctor.is_active === false;

  return (
    // The card body is a Link; `actions` render as a sibling row so buttons are
    // never nested inside the anchor.
    <div
      className={`group flex flex-col bg-white border rounded-xl p-4 transition-all ${
        archived
          ? "border-slate-200 opacity-70"
          : `hover:-translate-y-0.5 hover:shadow-md ${
              isCatA
                ? "border-callisto-gold/40 shadow-[0_0_0_1px_rgba(201,168,76,0.05)]"
                : "border-callisto"
            }`
      }`}
      data-testid={`doctor-card-${doctor.id}`}
    >
      <Link to={`/doctors/${doctor.id}`} className="flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-callisto-navy leading-tight truncate">
              {doctor.name}
            </h3>
            <p className="text-xs text-callisto-muted mt-0.5 truncate">
              {joinParts([doctor.specialization, doctor.qualification])}
            </p>
          </div>
          <CategoryBadge category={doctor.category} />
        </div>

        <div className="mt-3 space-y-1.5 text-xs text-callisto-body">
          <div className="flex items-center gap-1.5 min-w-0">
            <Building2 size={13} className="shrink-0 text-callisto-navy/60" />
            <span className="truncate">{doctor.clinic_hospital_name}</span>
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            <MapPin size={13} className="shrink-0 text-callisto-navy/60" />
            {/* RC-10: `{unit || area}{city ? ", " + city : ""}` produced the
                orphan ", Patna" whenever the unit was missing, and rendered an
                unmigrated free-text value as though it were a unit. */}
            <TerritoryLabel
              value={doctor.territory_unit || doctor.area}
              unmigrated={doctor.territory_unmigrated}
              suffixes={[doctor.city]}
              review="icon"
              className="truncate"
              testId={`doctor-territory-${doctor.id}`}
            />
          </div>
          {archived && (
            <p className="text-orange-600 font-semibold text-[11px]">Archived</p>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-callisto flex items-center justify-between gap-2 text-[11px]">
          {/* D12: a doctor with no company rule AND no stored target is
              UNSET, and the API serves null for it. Rendering that raw put a
              blank beside "/ month"; rendering 0 would say "do not visit". */}
          <span className="inline-flex items-center gap-1 text-callisto-muted">
            <Target size={12} />
            {doctor.monthly_visit_target == null ? (
              <span data-testid={`visit-target-unset-${doctor.id}`}>
                No visit target set
              </span>
            ) : (
              <>
                <span className="font-semibold text-callisto-navy">
                  {doctor.monthly_visit_target}
                </span>
                <span>/ month</span>
              </>
            )}
          </span>
          <span className="inline-flex items-center gap-1 text-callisto-muted truncate min-w-0">
            <UserCheck size={12} className="shrink-0" />
            <span className="truncate">{assignedName}</span>
          </span>
        </div>
      </Link>

      {actions && <div className="mt-3 flex gap-2">{actions}</div>}
    </div>
  );
}
